import React from 'react';

import { Skeleton } from 'antd';
import { Popup } from 'antd-mobile';
import {
  Avatar,
  AvatarShape,
  Column,
  Notification,
  Radio,
  RadioGroup,
  Row,
  Space,
  StarFilled,
  Text,
} from 'farmhub-ui-core';

import { useAppDispatch, useAppSelector } from 'app';
import { dataEmpty, Empty } from 'components';
import { Line } from 'containers/WarehouseOperation/WorkSpace/components';
import { findProductPricePolicy, selectProductUnit } from 'slice';
import { theme } from 'styled';
import {
  buildProductWholesaleUnit,
  formatImageUrl,
  formatMoney,
  getDummyArray,
  IMAGETYPE,
} from 'utils';

import { CTag, WrapList } from '../ProductStep/styles';
import {
  HeaderPopup,
  HeaderTitlePopup,
  WrapContent,
  WrapItem,
  WrapListProduct,
  WrapRating,
} from './styles';

interface PricePolicyStepProps {
  pricePolicies: any[];
  selectedPricePolicyID: string;
  setSelectedPricePolicyID: (e: string) => void;
}

const PricePolicyStepMobile = ({
  pricePolicies,
  selectedPricePolicyID,
  setSelectedPricePolicyID,
}: PricePolicyStepProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = React.useState(true);
  const [productPricePolicies, setProductPricePolicies] = React.useState([]);
  const [isShowPopup, setIsShowPopup] = React.useState(false);
  const [selectedShowProduct, setSelectedShowProduct] = React.useState<any>();
  const productUnit = useAppSelector(selectProductUnit);

  const onChangePolicy = (value: any): void => {
    setSelectedPricePolicyID(value.target.value);
  };

  const onChangePolicyProduct = (value: any): void => {
    setSelectedShowProduct({ name: value.name, id: value.id });
  };

  React.useEffect(() => {
    const initData = async (): Promise<void> => {
      try {
        setIsLoading(true);
        const result: any = await dispatch(
          findProductPricePolicy({
            select: [
              'id',
              'pricePolicyID',
              'price',
              'standardPrice',
              'createdAt',
              'status',
              'priceUnit',
              'product.id',
              'product.name',
              'product.price',
              'product.avatar',
              'product.unitPrefix',
              'product.unitWeight',
              'product.unitPostfix',
            ],
            relations: ['product'],
            order: { createdAt: 0 },
            where: { pricePolicyID: selectedShowProduct?.pricePolicyID },
          })
        ).unwrap();
        const { data } = result;
        const productPricePolicyList = data.map(item => {
          const [standardPriceUnit] = item.priceUnit.filter(
            val => val.isStandard
          );
          return {
            ...item,
            standardPriceUnit,
          };
        });
        setProductPricePolicies(productPricePolicyList);
      } catch (error: any) {
        Notification('error', 'Tải sản phẩm thất bại', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (selectedShowProduct?.id) {
      initData();
    }
  }, [dispatch, selectedShowProduct]);

  const ShowPopup = (): void => {
    setIsShowPopup(true);
  };

  return (
    <WrapContent>
      <WrapList>
        <RadioGroup
          styles={{ width: '100%' }}
          value={selectedPricePolicyID}
          onChange={onChangePolicy}
        >
          {pricePolicies.map((item: any) => (
            <>
              <WrapItem
                key={item.id}
                className={
                  selectedPricePolicyID == item.id ? 'selected' : 'nonSelected'
                }
              >
                <Column
                  alignItems="center"
                  justifyContent="space-between"
                  width="80%"
                >
                  <Row>
                    <Text
                      lineNumber={1}
                      style={{ fontWeight: 500, fontSize: '14px' }}
                    >
                      {item.name}
                    </Text>
                    <Text
                      color="price"
                      style={{
                        fontWeight: 600,
                        marginLeft: 6,
                        fontSize: '14px',
                        position: 'relative',
                      }}
                    >
                      {item.rating}
                    </Text>
                    <StarFilled
                      style={{ fontSize: 12, color: theme.colors.startFill }}
                    />
                  </Row>
                  <WrapRating>
                    <Text
                      style={{
                        color: `${theme.colors.primary.main}`,
                        fontSize: '14px',
                        padding: 0,
                        textDecoration: 'underline',
                        textUnderlineOffset: 3,
                        cursor: 'pointer',
                      }}
                      weight="regular"
                      onClick={() => {
                        onChangePolicyProduct({ id: item.id, name: item.name });
                        ShowPopup();
                      }}
                    >
                      Xem chi tiết
                    </Text>
                  </WrapRating>
                </Column>
                <Radio key={item.id} value={item.id}>
                  {' '}
                </Radio>
              </WrapItem>
              <Line
                color="#d9d9d9"
                height="1px"
                margin="0 3px"
                vertical={false}
                width="95%"
              />
            </>
          ))}
        </RadioGroup>

        {pricePolicies.length === 0 && (
          <Empty
            description="Hiện tại không có chính sách"
            img={dataEmpty.DEFAULT}
          />
        )}
        {pricePolicies.length > 7 && <Space height={50} />}
      </WrapList>
      <Popup
        bodyStyle={{
          borderTopLeftRadius: '8px',
          borderTopRightRadius: '8px',
          height: '60vh',
          maxHeight: '-webkit-fill-available',
          padding: '18px',
          display: 'flex',
          flexDirection: 'column',
        }}
        style={{
          zIndex: 1100,
        }}
        visible={isShowPopup}
        onClose={() => setIsShowPopup(false)}
        onMaskClick={() => setIsShowPopup(false)}
      >
        <HeaderPopup>
          {isLoading ? (
            <Skeleton.Input style={{ width: 100, height: 24 }} />
          ) : (
            <Text color="dark" style={{ fontSize: 14 }} weight="bold">
              {selectedShowProduct?.name || 'Chính sách giá'}
            </Text>
          )}
        </HeaderPopup>
        <Space height={6} />
        <WrapListProduct>
          {isLoading
            ? getDummyArray(6).map(item => (
                <>
                  <HeaderTitlePopup key={item.key}>
                    <Row styles={{ gap: 18 }}>
                      <Column width="fit-content">
                        <Skeleton.Avatar
                          shape="square"
                          size={52}
                          style={{ borderRadius: 8 }}
                        />
                      </Column>
                      <Column
                        alignItems="start"
                        justifyContent="start"
                        styles={{ height: '52px' }}
                      >
                        <Skeleton.Input style={{ width: 140, height: 20 }} />
                        <Space height={6} />
                        <Skeleton.Input style={{ width: 60, height: 20 }} />
                      </Column>
                    </Row>
                    <Column alignItems="flex-end" justifyContent="flex-end">
                      <Skeleton.Input style={{ width: 80, height: 20 }} />
                    </Column>
                  </HeaderTitlePopup>
                  <Space height={12} />
                </>
              ))
            : productPricePolicies.map(
                (productPricePolicy: any, index: number) => (
                  <>
                    <HeaderTitlePopup>
                      <Row styles={{ gap: 12 }}>
                        <Column width="fit-content">
                          <Avatar
                            shape={AvatarShape.Square}
                            size={48}
                            src={formatImageUrl(
                              productPricePolicy?.product?.avatar,
                              IMAGETYPE.AVATAR.xxs
                            )}
                          />
                        </Column>
                        <Column
                          alignItems="flex-start"
                          justifyContent="space-between"
                          styles={{ height: '42px' }}
                        >
                          <Text
                            color="primary"
                            lineNumber={1}
                            styles={{ fontSize: 13 }}
                            weight="bold"
                          >
                            {productPricePolicy?.product?.name}
                          </Text>
                          <Row>
                            <Text color="main" weight="bold">
                              {`${buildProductWholesaleUnit({
                                unitPrefix:
                                  productUnit.prefixObj[
                                    productPricePolicy?.standardPriceUnit
                                      ?.prefix
                                  ],
                                unitWeight:
                                  productPricePolicy?.standardPriceUnit?.weight,
                                unitPostfix:
                                  productUnit.postfixObj[
                                    productPricePolicy?.standardPriceUnit
                                      ?.postfix
                                  ],
                              })}`}
                            </Text>
                          </Row>
                        </Column>
                        <Column
                          alignItems="flex-end"
                          justifyContent="flex-end"
                          styles={{ gap: 6 }}
                        >
                          {productPricePolicy?.priceUnit?.map(
                            (priceUnit: any) => (
                              <CTag key={priceUnit.id}>
                                {formatMoney(priceUnit.price)}đ/
                                {buildProductWholesaleUnit({
                                  unitPrefix:
                                    productUnit.prefixObj[priceUnit.prefix],
                                  unitWeight: priceUnit.weight,
                                  unitPostfix:
                                    productUnit.postfixObj[priceUnit.postfix],
                                })}
                              </CTag>
                            )
                          )}
                        </Column>
                      </Row>
                    </HeaderTitlePopup>
                    {index < productPricePolicies.length - 1 && (
                      <Line
                        color="#d9d9d9"
                        height="1px"
                        margin="0px"
                        vertical={false}
                      />
                    )}
                  </>
                )
              )}
        </WrapListProduct>

        {productPricePolicies.length == 0 && !isLoading && (
          <Empty
            description="Hiện tại không có sản phẩm"
            img={dataEmpty.CATEGORY}
          />
        )}
      </Popup>
    </WrapContent>
  );
};

export default PricePolicyStepMobile;
